import { z } from 'zod'

/** Planlanan ödeme (nakit gider planı) şemaları — backend, web ve mobil aynı sözleşmeyi kullanır */

const money = z.string().regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir tutar giriniz.')
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Geçerli bir tarih giriniz (YYYY-AA-GG).')

export const SCHEDULED_PAYMENT_STATUSES = ['PLANNED', 'PAID', 'CANCELLED'] as const
export type ScheduledPaymentStatus = typeof SCHEDULED_PAYMENT_STATUSES[number]

/** Alıcının bağlanabileceği kayıt türleri (bağ yoksa `payee` serbest metindir) */
export const SCHEDULED_PAYEE_TYPES = ['SUB_CONSULTANT', 'CUSTOMER', 'VENDOR', 'USER'] as const
export type ScheduledPayeeType = typeof SCHEDULED_PAYEE_TYPES[number]

/**
 * Tekrar kuralı: her `interval` günde/haftada/ayda bir.
 * Bitiş `count` (toplam vade sayısı) ya da `until` (son tarih) ile verilir; ikisinden tam biri zorunludur.
 */
export const ScheduledPaymentRecurrenceSchema = z.object({
  unit: z.enum(['DAY', 'WEEK', 'MONTH']),
  interval: z.number().int().min(1).max(365),
  count: z.number().int().min(2).max(120).optional(),
  until: isoDate.optional(),
}).refine((r) => (r.count === undefined) !== (r.until === undefined), {
  message: 'Tekrar sayısı ya da bitiş tarihinden tam biri girilmelidir.',
  path: ['count'],
})
export type ScheduledPaymentRecurrence = z.infer<typeof ScheduledPaymentRecurrenceSchema>

const base = {
  title: z.string().min(2, 'Başlık zorunludur.').max(200),
  amount: money,
  currency: z.string().length(3).default('TRY'),
  dueDate: isoDate,
  /** Gider kategorisi (genel gider kategorileriyle aynı kodlar); ödendi işaretlenirken gider kaydı bu kategoriyle açılır */
  category: z.string().max(30).nullable().optional(),
  payee: z.string().max(200).nullable().optional(),
  payeeType: z.enum(SCHEDULED_PAYEE_TYPES).nullable().optional(),
  payeeId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
}

export const CreateScheduledPaymentSchema = z.object({
  ...base,
  recurrence: ScheduledPaymentRecurrenceSchema.nullable().optional(),
})
export type CreateScheduledPaymentInput = z.infer<typeof CreateScheduledPaymentSchema>

/** Tek bir vadeyi günceller (tekrar kuralı sonradan değişmez; seri için silip yeniden oluşturulur) */
export const UpdateScheduledPaymentSchema = z.object({
  title: base.title.optional(),
  amount: base.amount.optional(),
  currency: base.currency.optional(),
  dueDate: base.dueDate.optional(),
  category: base.category,
  payee: base.payee,
  payeeType: base.payeeType,
  payeeId: base.payeeId,
  projectId: base.projectId,
  notes: base.notes,
})
export type UpdateScheduledPaymentInput = z.infer<typeof UpdateScheduledPaymentSchema>

/** Ödendi işareti; `createExpense` açıksa genel gider kaydı otomatik açılır (yalnızca TRY ve kategorili planlarda) */
export const CompleteScheduledPaymentSchema = z.object({
  paidDate: isoDate,
  paidAmount: money.optional(),
  createExpense: z.boolean().default(false),
})
export type CompleteScheduledPaymentInput = z.infer<typeof CompleteScheduledPaymentSchema>
